import { envConfig } from '#constants/envConfig';
import { seo } from '#utils/seo';

/**
 * head() for the blog index.
 *
 * The route used to declare nothing, so /blog and /ru/blog inherited the site-wide title
 * ("Teamplay.gg - Looking for group (LFG) community for gamers.") and shipped with no
 * canonical at all: two localized listings competing as the same untitled page.
 *
 * Canonical is per locale with hreflang alternates, matching what sitemap.xml already
 * declares for these two URLs. Articles are the opposite case and stay EN-canonical: the
 * post body is the same English text on both locales, so /ru/blog/<slug> is a duplicate,
 * while the listing itself is genuinely localized chrome.
 */
const COPY = {
  en: {
    title: 'Teamplay Blog - LFG Guides & Game News',
    description:
      'Guides on finding teammates, ranked play and squads, plus news from Apex, Dota 2, Fortnite and Valorant. Updates from the Teamplay LFG platform.',
    name: 'Teamplay Blog',
    home: 'Teamplay',
    crumb: 'Blog',
  },
  ru: {
    title: 'Блог Тимплей - гайды по поиску тиммейтов и новости игр',
    description:
      'Гайды по поиску тиммейтов, рангу и игре в пати, плюс новости Apex, Dota 2, Fortnite и Valorant. Что нового на LFG-платформе Тимплей.',
    name: 'Блог Тимплей',
    home: 'Тимплей',
    crumb: 'Блог',
  },
} as const;

export const blogListHead = (locale: string | undefined) => {
  const activeLocale = locale === 'ru' ? 'ru' : 'en';
  const copy = COPY[activeLocale];

  const enUrl = `${envConfig.HOST}/blog`;
  const ruUrl = `${envConfig.HOST}/ru/blog`;
  const canonicalUrl = activeLocale === 'ru' ? ruUrl : enUrl;

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Blog',
      name: copy.name,
      description: copy.description,
      url: canonicalUrl,
      inLanguage: activeLocale,
      publisher: {
        '@type': 'Organization',
        name: 'Teamplay',
        url: envConfig.HOST,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: copy.home, item: envConfig.HOST },
        { '@type': 'ListItem', position: 2, name: copy.crumb, item: canonicalUrl },
      ],
    },
  ];

  return {
    meta: seo({
      title: copy.title,
      description: copy.description,
      image: `${envConfig.HOST}/preview.png`,
      url: canonicalUrl,
    }),
    links: [
      { rel: 'canonical', href: canonicalUrl },
      { rel: 'alternate', hrefLang: 'en', href: enUrl },
      { rel: 'alternate', hrefLang: 'ru', href: ruUrl },
      { rel: 'alternate', hrefLang: 'x-default', href: enUrl },
    ],
    scripts: jsonLd.map((schema) => ({
      type: 'application/ld+json',
      children: JSON.stringify(schema),
    })),
  };
};
